import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, imageUrl } from "../lib/api";
import { useCart } from "../context/CartContext";

const field = "w-full border border-border bg-background px-4 py-3 font-body text-sm focus:outline-none focus:border-foreground transition-colors";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [rzpEnabled, setRzpEnabled] = useState(false);
  const [upi, setUpi] = useState({ id: "", name: "" });
  const [promo, setPromo] = useState({ enabled: false, discount: 0 });
  const [method, setMethod] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(false);
  const [tracking, setTracking] = useState("");
  const [upiRef, setUpiRef] = useState("");
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", address: "", city: "", pincode: "" });

  useEffect(() => {
    api.get("/config").then((r) => {
      setRzpEnabled(r.data.razorpay_enabled);
      setUpi({ id: r.data.upi_id || "", name: r.data.upi_name || "" });
      setPromo({ enabled: !!r.data.promo_enabled, discount: r.data.promo_discount || 0 });
      setMethod(r.data.razorpay_enabled ? "razorpay" : "upi");
    }).catch(() => {});
  }, []);

  const subtotal = total;
  const discountPct = appliedCoupon ? appliedCoupon.discount_percent : (promo.enabled ? promo.discount : 0);
  const discountAmt = Math.round(subtotal * discountPct / 100);
  const payable = subtotal - discountAmt;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon.trim() });
      setAppliedCoupon(data);
      toast.success(`Coupon applied — ${data.discount_percent}% off`);
    } catch (err) {
      setAppliedCoupon(null);
      toast.error(err.response?.data?.detail || "Invalid coupon");
    }
  };

  const upiLink = upi.id ? `upi://pay?pa=${encodeURIComponent(upi.id)}&pn=${encodeURIComponent(upi.name)}&am=${payable}&cu=INR` : "";

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.customer_name && form.email && form.phone && form.address && form.city && form.pincode;

  const placeOrder = async () => {
    if (!valid) { toast.error("Please fill all details"); return; }
    if (method === "upi" && !upiRef.trim()) { toast.error("Please enter your UPI transaction / UTR number after paying"); return; }
    setPlacing(true);
    try {
      const payload = { items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, qty: i.qty, size: i.size, image: i.image })), ...form, payment_method: method, upi_ref: method === "upi" ? upiRef.trim() : null, coupon_code: appliedCoupon ? appliedCoupon.code : null };
      const { data } = await api.post("/orders", payload);

      if (data.mode === "razorpay") {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment gateway"); setPlacing(false); return; }
        const rzp = new window.Razorpay({
          key: data.key_id, amount: data.amount, currency: "INR", name: "Navrang Udaipur",
          description: "Sanganeri Shirts", order_id: data.razorpay_order_id,
          prefill: { name: form.customer_name, email: form.email, contact: form.phone },
          theme: { color: "#9C3D32" },
          handler: async (resp) => {
            try {
              await api.post("/orders/verify", { order_id: data.order_id, razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
              clear(); setTracking(data.tracking_id); setDone(true);
            } catch { toast.error("Payment verification failed"); }
          },
          modal: { ondismiss: () => setPlacing(false) },
        });
        rzp.open();
      } else {
        clear(); setTracking(data.tracking_id); setDone(true);
      }
    } catch (e) {
      toast.error("Could not place order");
      setPlacing(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-40 text-center max-w-lg mx-auto px-6" data-testid="order-success">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.7 }}
          className="w-20 h-20 mx-auto bg-[hsl(var(--primary))] text-white rounded-full flex items-center justify-center">
          <Check className="w-9 h-9" />
        </motion.div>
        <h1 className="font-display text-5xl mt-8">Order placed</h1>
        <p className="font-body text-muted-foreground mt-4">Thank you! We'll reach out shortly on {form.phone} to confirm your Sanganeri shirts.</p>
        <div className="mt-6 border border-border bg-card p-5 inline-block">
          <p className="overline text-muted-foreground">Your tracking ID</p>
          <p className="font-display text-3xl text-[hsl(var(--primary))]" data-testid="tracking-id">{tracking}</p>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={`/track?id=${tracking}`} className="bg-[hsl(var(--primary))] text-white px-8 py-4 font-body text-sm" data-testid="track-now-btn">Track my order</Link>
          <Link to="/shop" className="border border-border px-8 py-4 font-body text-sm">Continue shopping</Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return <div className="pt-40 pb-40 text-center"><h1 className="font-display text-4xl">Your bag is empty</h1><Link to="/shop" className="mt-6 inline-block text-[hsl(var(--primary))] underline">Shop now</Link></div>;
  }

  return (
    <div className="pt-32 pb-28 max-w-[1200px] mx-auto px-6 md:px-12">
      <h1 className="font-display text-5xl md:text-6xl mb-12">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          <p className="overline text-[hsl(var(--primary))]">Shipping details</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <input className={field} placeholder="Full name" value={form.customer_name} onChange={upd("customer_name")} data-testid="input-name" />
            <input className={field} placeholder="Phone" value={form.phone} onChange={upd("phone")} data-testid="input-phone" />
          </div>
          <input className={field} placeholder="Email" value={form.email} onChange={upd("email")} data-testid="input-email" />
          <input className={field} placeholder="Address" value={form.address} onChange={upd("address")} data-testid="input-address" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input className={field} placeholder="City" value={form.city} onChange={upd("city")} data-testid="input-city" />
            <input className={field} placeholder="Pincode" value={form.pincode} onChange={upd("pincode")} data-testid="input-pincode" />
          </div>

          <p className="overline text-[hsl(var(--primary))] pt-4">Payment</p>
          <div className="space-y-3" data-testid="payment-methods">
            {rzpEnabled && (
              <label className={`flex items-center gap-3 border p-4 cursor-pointer ${method === "razorpay" ? "border-foreground" : "border-border"}`}>
                <input type="radio" name="pay" checked={method === "razorpay"} onChange={() => setMethod("razorpay")} data-testid="pay-razorpay" />
                <span className="font-body text-sm">Pay online (Razorpay — UPI / Card / Netbanking)</span>
              </label>
            )}
            {upi.id && (
              <div className={`border p-4 ${method === "upi" ? "border-foreground" : "border-border"}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" name="pay" checked={method === "upi"} onChange={() => setMethod("upi")} data-testid="pay-upi" />
                  <span className="font-body text-sm">Prepaid via UPI (GPay / PhonePe / Paytm)</span>
                </label>
                {method === "upi" && (
                  <div className="mt-4 flex flex-col sm:flex-row gap-5 items-start" data-testid="upi-box">
                    {upiLink && <div className="bg-white p-2 border border-border shrink-0"><QRCodeCanvas value={upiLink} size={160} level="M" data-testid="upi-qr" /></div>}
                    <div className="flex-1">
                      <p className="overline text-muted-foreground">Pay ₹{payable} to</p>
                      <p className="font-display text-2xl">{upi.id}</p>
                      <p className="font-body text-xs text-muted-foreground mt-1">{upi.name}</p>
                      <a href={upiLink} className="mt-2 inline-block text-sm text-[hsl(var(--primary))] link-underline sm:hidden">Open UPI app →</a>
                      <p className="font-body text-xs text-muted-foreground mt-4">After paying, enter the 12-digit UPI transaction / UTR number below. We'll verify & confirm your order.</p>
                      <input className={`${field} mt-2`} placeholder="UPI transaction / UTR number" value={upiRef} onChange={(e) => setUpiRef(e.target.value)} data-testid="upi-ref" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="border border-border p-8 bg-card sticky top-28">
            <h2 className="font-display text-3xl mb-6">Order</h2>
            <div className="space-y-3 max-h-64 overflow-auto mb-4">
              {items.map((it) => (
                <div key={it.key || it.product_id} className="flex gap-3 items-center">
                  <img src={imageUrl(it.image)} alt={it.name} className="w-12 h-16 object-cover bg-muted" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm truncate">{it.name}</p>
                    <p className="overline text-muted-foreground">{it.size ? `${it.size} · ` : ""}Qty {it.qty}</p>
                  </div>
                  <span className="font-body text-sm">₹{it.price * it.qty}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mb-4" data-testid="coupon-box">
              <input className="flex-1 border border-border bg-background px-3 py-2 font-body text-sm uppercase focus:outline-none focus:border-foreground" placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input" />
              <button onClick={applyCoupon} className="px-4 border border-border font-body text-sm hover:border-foreground transition-colors" data-testid="coupon-apply">Apply</button>
            </div>
            {appliedCoupon && <p className="overline text-[hsl(var(--primary))] mb-2" data-testid="coupon-applied">✓ {appliedCoupon.code} — {appliedCoupon.discount_percent}% off</p>}
            <div className="flex justify-between font-body font-600 text-lg py-4 border-t border-border"><span>Total</span><span>₹{total}</span></div>
            {discountPct > 0 && (
              <div className="-mt-2 mb-3 space-y-1" data-testid="checkout-discount">
                <div className="flex justify-between font-body text-sm text-muted-foreground"><span>Offer ({discountPct}% off)</span><span>−₹{discountAmt}</span></div>
                <div className="flex justify-between font-body font-600 text-lg text-[hsl(var(--primary))]"><span>Payable</span><span>₹{payable}</span></div>
              </div>
            )}
            <button onClick={placeOrder} disabled={placing} data-testid="place-order-btn"
              className="w-full bg-[hsl(var(--primary))] text-white px-8 py-4 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors duration-400 disabled:opacity-60">
              {placing ? "Processing…" : method === "razorpay" ? `Pay ₹${payable}` : method === "upi" ? "I've paid — Place order" : "Place order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
